import { SurveyAnswers } from "../../common/hooks/api/useSurvey";
import { PrivacyCenterDto } from "../../common/service/server/dto/PrivacyCenterDto";
import { PrivacyCenterSettingsProps } from "../components/superorg/settings/PrivacyCenterSettings";
import {
  HAS_DATA_PRIVACY_OFFICER_SLUG,
  HAS_DESIGNATED_REP_SLUG,
  hasDesignatedRepresentative,
  isDataPrivacyOfficerRequired,
} from "./eeaUkSurvey";
import { SurveyQuestionProps } from "../components/get-compliant/survey/SurveyQuestion";
import { hasAnswered, QuestionWithVisibility } from "./survey";

/** Everything beyond the raw answer that decides whether a Business Profile question is done. */
export type BusinessProfileAnswerContext = {
  isDpoRequired: boolean;
  hasDpo: boolean;
  dpoDoesNotApply: boolean;
  hasDesignatedRep: boolean;
  eeaContactDoesNotApply: boolean;
  hasDesignatedRepTask: boolean;
};

/**
 * The one rule for "is this question answered?" on the Business Profile page. The per-question
 * Incomplete badge and each section's header badge both read it, so they can never disagree
 * (they used to: the header accepted a plain "No" to the DPO / designated-rep questions and
 * counted any non-empty string as an answer, while the rows did neither).
 */
export function isBusinessProfileQuestionAnswered(
  question: SurveyQuestionProps,
  answers: SurveyAnswers,
  ctx: BusinessProfileAnswerContext,
): boolean {
  if (question.slug === HAS_DATA_PRIVACY_OFFICER_SLUG) {
    // Only a required DPO can be incomplete (mirrors OrganizationOnboardingProgressService). Saved
    // details, "does not apply", or "No" (do this later: the backend opens an Add DPO task) count;
    // a "Yes" without details, or no answer at all, does not.
    return (
      !ctx.isDpoRequired || ctx.hasDpo || ctx.dpoDoesNotApply || answers[question.slug] === "false"
    );
  }
  if (question.slug === HAS_DESIGNATED_REP_SLUG) {
    // Same shape as the DPO question: "No" (do this later) and "does not apply" both count.
    return (
      ctx.hasDesignatedRep ||
      ctx.eeaContactDoesNotApply ||
      ctx.hasDesignatedRepTask ||
      answers[question.slug] === "false"
    );
  }
  return hasAnswered(answers, question, /* debug = */ false);
}

export function computeBusinessProfileSurveyCompletion({
  featureGdpr,
  privacyCenter,
  pcSettings,
  bizVisibleQuestions,
  bizAnswers,
  hrVisibleQuestions,
  hrAnswers,
  eeaVisibleQuestions,
  eeaAnswers,
  financialIncentiveAnswer,
}: {
  featureGdpr: boolean | undefined;
  privacyCenter: PrivacyCenterDto | undefined;
  pcSettings: PrivacyCenterSettingsProps;
  bizVisibleQuestions: QuestionWithVisibility[];
  bizAnswers: SurveyAnswers;
  hrVisibleQuestions: QuestionWithVisibility[];
  hrAnswers: SurveyAnswers;
  eeaVisibleQuestions: QuestionWithVisibility[];
  eeaAnswers: SurveyAnswers;
  financialIncentiveAnswer: unknown;
}): {
  isBizSurveyComplete: boolean;
  isHrSurveyComplete: boolean;
  isEeaUkSurveyComplete: boolean;
  isFinancialIncentivesSectionComplete: boolean;
  isSurveyComplete: boolean;
} {
  const ctx: BusinessProfileAnswerContext = {
    isDpoRequired: Boolean(featureGdpr && isDataPrivacyOfficerRequired(eeaAnswers, privacyCenter)),
    hasDpo: Boolean(pcSettings?.dpoName) && Boolean(pcSettings?.dpoEmail),
    dpoDoesNotApply: Boolean(pcSettings?.dpoDoesNotApply ?? privacyCenter?.dpoOfficerDoesNotApply),
    hasDesignatedRep: hasDesignatedRepresentative(pcSettings),
    eeaContactDoesNotApply: Boolean(
      pcSettings?.eeaContactDoesNotApply ?? privacyCenter?.eeaContactDoesNotApply,
    ),
    hasDesignatedRepTask: Boolean(privacyCenter?.hasDesignatedRepTask),
  };

  const isBizSurveyComplete = bizVisibleQuestions.every((q) =>
    isBusinessProfileQuestionAnswered(q, bizAnswers, ctx),
  );
  const isHrSurveyComplete = hrVisibleQuestions.every((q) =>
    isBusinessProfileQuestionAnswered(q, hrAnswers, ctx),
  );

  const isFinancialIncentivesSectionComplete =
    financialIncentiveAnswer !== undefined && financialIncentiveAnswer !== null;

  const isEeaUkSurveyComplete =
    !featureGdpr ||
    eeaVisibleQuestions.every((q) => isBusinessProfileQuestionAnswered(q, eeaAnswers, ctx));

  const isSurveyComplete =
    isBizSurveyComplete &&
    isHrSurveyComplete &&
    isEeaUkSurveyComplete &&
    isFinancialIncentivesSectionComplete;

  return {
    isBizSurveyComplete,
    isHrSurveyComplete,
    isEeaUkSurveyComplete,
    isFinancialIncentivesSectionComplete,
    isSurveyComplete,
  };
}
